<template>
  <EmptyState
    v-if="!team && teamQuery.error.value"
    class="team-empty"
    icon="alert-triangle"
    color="red"
    :title="`Couldn't load this ${noun}'s team`"
    description="Please try again."
  >
    <template #action>
      <Button variant="outline" size="medium" :loading="teamQuery.isLoading.value" @click="teamQuery.refetch()">
        Retry
      </Button>
    </template>
  </EmptyState>

  <div v-else class="team-layout">
    <TeamRail
      class="team-rail"
      :sections="railSections"
      :noun="noun"
      :active="team ? activeSection : undefined"
      :note="railNote"
      @select="scrollToSection"
    />

    <View v-if="!team" justify="center" padding="lg">
      <LoadingIndicator />
    </View>

    <View v-else direction="column" gap="md" class="team-sections">
      <TeamSection
        v-if="team.accessRequests?.length"
        id="team-requests"
        :title="`Asking to work on this ${noun}`"
        :description="`These teammates can view this ${noun} but can't change it. Add one as a collaborator and Tallo emails them that they can start, or decline the request.`"
      >
        <TeamPersonRow
          v-for="request in team.accessRequests"
          :key="request.id"
          :name="nameOf(request.requester)"
          :details="request.requester.email"
          :meta="`Asked ${formatDistanceToNowStrict(new Date(request.createdAt), { addSuffix: true })}`"
        >
          <template #action>
            <View gap="sm">
              <Button size="small" :loading="answering === request.id" @click="grantRequest(request)">
                Add as collaborator
              </Button>
              <Button
                variant="ghost"
                size="small"
                :disabled="answering === request.id"
                @click="declineRequest(request)"
              >
                Decline
              </Button>
            </View>
          </template>
        </TeamPersonRow>
      </TeamSection>

      <TeamSection
        id="team-manager"
        title="Manager"
        :description="
          subject.kind === 'building'
            ? 'The manager runs this building: they can reassign it, change its collaborators and move it.'
            : 'Tallo sends this unit\'s showing requests, renter questions and reminders to its manager, and puts its showings on their calendar.'
        "
      >
        <template #action>
          <Button v-if="canReassign" variant="outline" size="small" @click="handOffModalRef?.open()">Reassign</Button>
        </template>

        <TeamPersonRow
          v-if="team.manager"
          :name="nameOf(team.manager)"
          :details="team.manager.email"
          :is-you="isYou(team.manager.investorId)"
          :badges="badgesFor(team.manager.investorId, 'manager')"
        />
        <Text v-else variant="body-medium" color="secondary">No manager right now.</Text>
      </TeamSection>

      <TeamSection id="team-listing-contact" title="Listing contact" :description="listingContactDescription">
        <TeamPersonRow
          v-if="team.listingContact.contact"
          :name="nameOf(team.listingContact.contact)"
          :details="team.listingContact.contact.email"
          :meta="listingContactSource"
          :is-you="isYou(team.listingContact.contact.investorId)"
        />
        <Text v-else variant="body-medium" color="secondary">Nobody right now.</Text>
      </TeamSection>

      <TeamSection
        id="team-portfolio"
        title="Portfolio"
        :description="`A portfolio's owner runs every ${noun} in it: they can reassign it, change its collaborators and move it. The portfolio's default collaborators join each ${noun} filed into it.`"
      >
        <TeamPersonRow
          v-if="team.portfolio"
          :name="team.portfolio.name"
          :details="portfolioLine"
          :badges="portfolioBadges"
        >
          <template #leading>
            <IconTile icon="folder" color="neutral" shape="rounded" size="small" />
          </template>
          <template #action>
            <Button v-if="canMove" variant="outline" size="small" @click="moveModalRef?.open()">Move</Button>
          </template>
        </TeamPersonRow>

        <EmptyState
          v-else
          class="team-empty"
          icon="folder"
          color="neutral"
          title="Not in a portfolio"
          :description="`This ${noun} stands on its own, so only its manager and an admin can change its team. File it into a portfolio and its owner runs it, and the portfolio's default collaborators join it right away.`"
        >
          <template v-if="canMove" #action>
            <Button variant="outline" size="medium" @click="moveModalRef?.open()">Move to portfolio</Button>
          </template>
        </EmptyState>
      </TeamSection>

      <TeamSection
        id="team-collaborators"
        title="Collaborators"
        :description="
          subject.kind === 'building'
            ? 'Collaborators work on this building like its manager. Notifications and calendar events stay with the manager.'
            : 'Collaborators run this unit like its manager, from showings to applications and listing edits, and see it on their home page and in their digest. Notifications and calendar events stay with the manager.'
        "
      >
        <template #action>
          <Button v-if="canAddCollaborator" variant="outline" size="small" @click="addCollaboratorModalRef?.open()">
            Add collaborator
          </Button>
          <Text v-else-if="team.myAccessRequest" variant="body-small" color="secondary" data-testid="access-requested">
            You asked to work on it {{ askedAgo }}
          </Text>
          <Button
            v-else-if="team.canManage === false"
            variant="outline"
            size="small"
            data-testid="team-request-access"
            @click="accessDialog.open(subject.id, subject.kind)"
          >
            Request access
          </Button>
        </template>

        <TeamPersonRow
          v-for="collaborator in team.collaborators"
          :key="collaborator.investorId"
          :name="nameOf(collaborator)"
          :details="collaborator.email"
          :meta="
            collaborator.joinsNewUnits
              ? `Also joins every unit filed into ${team.portfolio?.name ?? 'the portfolio'}`
              : undefined
          "
          :is-you="isYou(collaborator.investorId)"
          :badges="badgesFor(collaborator.investorId, 'collaborator')"
        >
          <template v-if="team.canEditCollaborators" #action>
            <MenuButton>
              <template #menu-button-trigger>
                <!-- MenuButton's trigger is the tab stop and the button; the icon button inside only draws and names it -->
                <IconButton
                  icon="dots-horizontal"
                  variant="ghost"
                  shape="square"
                  size="small"
                  tabindex="-1"
                  :aria-label="`${nameOf(collaborator)} actions`"
                />
              </template>
              <MenuButtonItem
                v-if="agents?.canEdit && !isAgent(collaborator.investorId)"
                title="Make showing agent"
                icon="calendar"
                @click="makeAgent(collaborator)"
              />
              <MenuButtonItem
                v-if="team.canHandOff"
                title="Make manager"
                icon="user-check-01"
                @click="handOffModalRef?.open(collaborator.investorId)"
              />
              <Divider
                v-if="(agents?.canEdit && !isAgent(collaborator.investorId)) || team.canHandOff"
                role="separator"
              />
              <MenuButtonItem
                :title="`Remove from ${noun}`"
                icon="trash"
                variant="dangerous"
                @click="confirmRemoveCollaborator(collaborator)"
              />
            </MenuButton>
          </template>
        </TeamPersonRow>

        <EmptyState
          v-if="!team.collaborators.length"
          class="team-empty"
          icon="users-01"
          color="orange"
          title="No collaborators yet"
          :description="
            subject.kind === 'building'
              ? 'Add a teammate and they can work on this building like the manager. Notifications stay with the manager.'
              : 'Add a teammate and this unit shows up on their home page and in their queues. They can run it like the manager; notifications stay with the manager.'
          "
        >
          <template v-if="canAddCollaborator" #action>
            <Button variant="outline" size="medium" @click="addCollaboratorModalRef?.open()">Add collaborator</Button>
          </template>
        </EmptyState>
      </TeamSection>

      <TeamSection
        id="team-agents"
        title="Showing agents"
        :description="`Who Tallo can book for tours of this ${noun}: anyone on your team, or an agent without a Tallo account. Renters are given the host's name and number, and Tallo texts the host if a renter cancels or can't get in.`"
      >
        <template #action>
          <Button
            v-if="agents?.canEdit && agents.agents.length"
            variant="outline"
            size="small"
            @click="addAgentModalRef?.open()"
          >
            Add agent
          </Button>
        </template>

        <EmptyState
          v-if="!agents && agentsQuery.error.value"
          class="team-empty"
          icon="alert-triangle"
          color="red"
          :title="`Couldn't load this ${noun}'s showing agents`"
          description="Please try again."
        >
          <template #action>
            <Button
              variant="outline"
              size="medium"
              :loading="agentsQuery.isLoading.value"
              @click="agentsQuery.refetch()"
            >
              Retry
            </Button>
          </template>
        </EmptyState>

        <View v-else-if="!agents" justify="center" padding="md">
          <LoadingIndicator />
        </View>

        <template v-else>
          <TeamPersonRow
            v-for="agent in agents.agents"
            :key="agent.id"
            :name="agentName(agent)"
            :details="agentDetails(agent)"
            :meta="
              agent.phone
                ? undefined
                : 'No phone number yet. Renters get the host\'s number, so add one before they can be picked.'
            "
            :is-you="isYou(agent.investorId)"
            :badges="badgesFor(agent.investorId, 'showingAgent')"
          >
            <template v-if="agents.canEdit" #action>
              <MenuButton>
                <template #menu-button-trigger>
                  <IconButton
                    icon="dots-horizontal"
                    variant="ghost"
                    shape="square"
                    size="small"
                    tabindex="-1"
                    :aria-label="`${agentName(agent)} actions`"
                  />
                </template>
                <MenuButtonItem
                  v-if="!agent.investorId"
                  title="Edit contact details"
                  icon="pencil"
                  @click="editAgentModalRef?.open(agent)"
                />
                <MenuButtonItem
                  v-else-if="!agent.phone"
                  title="Add phone number"
                  icon="phone"
                  @click="addPhoneModalRef?.open(agent)"
                />
                <Divider v-if="!agent.investorId || !agent.phone" role="separator" />
                <MenuButtonItem
                  title="Remove as agent"
                  icon="trash"
                  variant="dangerous"
                  @click="confirmRemoveAgent(agent)"
                />
              </MenuButton>
            </template>
          </TeamPersonRow>

          <EmptyState
            v-if="!agents.agents.length"
            class="team-empty"
            icon="calendar"
            color="blue"
            title="No showing agents"
            :description="`Tours of this ${noun} can't be confirmed until someone can host them. Add a teammate or an outside agent, or take it on yourself.`"
          >
            <template v-if="agents.canEdit" #action>
              <View gap="sm" wrap="wrap">
                <Button size="medium" @click="addAgentModalRef?.open()">Add agent</Button>
                <Button variant="ghost" size="medium" :loading="addAgent.isLoading.value" @click="makeMeTheAgent">
                  Make me the agent
                </Button>
              </View>
            </template>
          </EmptyState>
        </template>
      </TeamSection>
    </View>

    <template v-if="team">
      <MoveToPortfolioModal ref="moveModalRef" :subject="subject" :current-portfolio="team.portfolio" />
      <HandOffModal
        ref="handOffModalRef"
        :subject="subject"
        :members="members"
        :manager="team.manager"
        :manager-is-you="Boolean(team.manager) && isYou(team.manager!.investorId)"
        :hints="collaboratorHints"
        :unit-name="unitName"
      />
      <AddCollaboratorModal
        ref="addCollaboratorModalRef"
        :subject="subject"
        :members="members"
        :manager-id="team.manager?.investorId"
        :collaborator-ids="collaboratorIds"
        :hints="agentHints"
      />
      <AddUnitAgentModal
        ref="addAgentModalRef"
        :subject="subject"
        :members="members"
        :roster="roster"
        :listed="agents?.agents ?? []"
        :roles-here="rolesHere"
        :portfolio-name="subject.kind === 'property' ? team.portfolio?.name : undefined"
      />
    </template>
    <AddShowingAgentPhoneModal ref="addPhoneModalRef" />
    <EditShowingAgentModal ref="editAgentModalRef" @saved="announceSaved" />
    <ConfirmModal
      ref="confirmModalRef"
      width="30rem"
      :title="pendingRemoval?.title ?? ''"
      :description="pendingRemoval?.description ?? ''"
      confirm-label="Remove"
      :loading="isRemoving"
      @confirm="runRemoval"
      @after-close="onRemovalClosed"
    />
  </div>
</template>

<script setup lang="ts">
import { formatDistanceToNowStrict } from 'date-fns';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import {
  Button,
  ConfirmModal,
  Divider,
  EmptyState,
  IconButton,
  IconTile,
  LoadingIndicator,
  MenuButton,
  MenuButtonItem,
  Text,
  View,
  useAlertsStore,
} from '@tallo/design-system';
import {
  TeamMemberDto,
  teamMemberName as nameOf,
  useCompanyPortfoliosQuery,
  useCompanyTeamQuery,
} from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import {
  AddShowingAgentPhoneModal,
  EditShowingAgentModal,
  PropertyShowingAgentDto,
  ShowingAgentDto,
  useAddPropertyShowingAgent,
  useRemovePropertyShowingAgent,
  useShowingAgentsQuery,
  useTeamShowingAgentsQuery,
} from '@tallo/investor/showing-agents';
import { escapeHtml, formatPhoneNumber, getFullName } from '@tallo/utility';

import { PropertyAccessRequestDto, TeamSubject } from '../../dtos/property-team.dto.interface';
import {
  useAddPropertyCollaborator,
  useDeclinePropertyAccessRequest,
  useRemovePropertyCollaborator,
} from '../../mutations/property-team.mutation';
import { usePropertyTeamQuery } from '../../queries/property-team.query';
import { usePropertyAccessDialogStore } from '../../stores/property-access-dialog.store';
import { usePropertyStore } from '../../stores/property.store';
import AddCollaboratorModal from './add-collaborator-modal.vue';
import AddUnitAgentModal from './add-unit-agent-modal.vue';
import HandOffModal from './hand-off-modal.vue';
import MoveToPortfolioModal from './move-to-portfolio-modal.vue';
import TeamPersonRow from './team-person-row.vue';
import TeamRail from './team-rail.vue';
import { TeamRailSection, TeamRoleBadge, subjectNoun, teamRoleBadges } from './team-roles';
import TeamSection from './team-section.vue';

type Role = keyof typeof teamRoleBadges;

interface PendingRemoval {
  title: string;
  description: string;
  run: () => Promise<unknown>;
  done: string;
  /** Where the keyboard goes once the row that opened the dialog is gone */
  sectionId: string;
}

const props = defineProps<{ subject: TeamSubject }>();

const alertsStore = useAlertsStore();
const investorStore = useInvestorStore();
const propertyStore = usePropertyStore();
const teamQuery = usePropertyTeamQuery(() => props.subject);
const agentsQuery = useTeamShowingAgentsQuery(() => props.subject);
const { portfolios } = useCompanyPortfoliosQuery();
const { members } = useCompanyTeamQuery();
const { data: rosterData } = useShowingAgentsQuery();
const addAgent = useAddPropertyShowingAgent();
const removeAgent = useRemovePropertyShowingAgent();
const removeCollaborator = useRemovePropertyCollaborator();
const addCollaborator = useAddPropertyCollaborator();
const declineAccessRequest = useDeclinePropertyAccessRequest();
const accessDialog = usePropertyAccessDialogStore();
// The request being answered; the mutations are shared by every row, so their own flags would spin them all
const answering = ref<string>();

const moveModalRef = ref<InstanceType<typeof MoveToPortfolioModal>>();
const handOffModalRef = ref<InstanceType<typeof HandOffModal>>();
const addCollaboratorModalRef = ref<InstanceType<typeof AddCollaboratorModal>>();
const addAgentModalRef = ref<InstanceType<typeof AddUnitAgentModal>>();
const addPhoneModalRef = ref<InstanceType<typeof AddShowingAgentPhoneModal>>();
const editAgentModalRef = ref<InstanceType<typeof EditShowingAgentModal>>();
const confirmModalRef = ref<InstanceType<typeof ConfirmModal>>();
const pendingRemoval = ref<PendingRemoval>();
// The removal the closing dialog completed, if any; a cancelled one leaves the keyboard where the focus trap puts it
let completedRemoval: PendingRemoval | undefined;

const team = computed(() => teamQuery.data.value);
const noun = computed(() => subjectNoun(props.subject));
const askedAgo = computed(() =>
  team.value?.myAccessRequest
    ? formatDistanceToNowStrict(new Date(team.value.myAccessRequest.createdAt), { addSuffix: true })
    : '',
);
const agents = computed(() => agentsQuery.data.value);
const roster = computed(() => rosterData.value ?? []);
const isRemoving = computed(() => removeAgent.isLoading.value || removeCollaborator.isLoading.value);
const unitName = computed(() =>
  propertyStore.property?.id === props.subject.id ? propertyStore.property.displayName : undefined,
);

// With no portfolio in the company the only destination would be "No portfolio", so Move waits for the first one
const canMove = computed(() => Boolean(team.value?.canMove) && portfolios.value.length > 0);
// Reassigning or adding needs someone to pick: a team of one has nobody else to offer
const canReassign = computed(
  () =>
    Boolean(team.value?.canHandOff) &&
    members.value.some((member) => member.investorId !== team.value?.manager?.investorId),
);
const collaboratorIds = computed(() => team.value?.collaborators.map(({ investorId }) => investorId) ?? []);
const canAddCollaborator = computed(
  () =>
    Boolean(team.value?.canEditCollaborators) &&
    members.value.some(
      (member) =>
        member.investorId !== team.value?.manager?.investorId && !collaboratorIds.value.includes(member.investorId),
    ),
);
// Nothing on the tab is the viewer's to change, so the rail says who to ask instead
const canChangeTeam = computed(
  () =>
    Boolean(team.value?.canHandOff || team.value?.canEditCollaborators || team.value?.canMove) ||
    Boolean(agents.value?.canEdit),
);

const portfolioDetails = computed(() => portfolios.value.find(({ id }) => id === team.value?.portfolio?.id));
const portfolioLine = computed(() => {
  const owner = team.value?.portfolio?.owner;
  const collaborators = portfolioDetails.value?.collaborators.length ?? 0;

  return [
    owner && (isYou(owner.investorId) ? 'Owned by you' : `Owned by ${nameOf(owner)}`),
    collaborators && `${collaborators} ${collaborators === 1 ? 'collaborator' : 'collaborators'}`,
  ]
    .filter(Boolean)
    .join(' · ');
});
const portfolioBadges = computed<TeamRoleBadge[]>(() => {
  const count = portfolioDetails.value?.unitCount;

  return count ? [{ label: `${count} ${count === 1 ? 'property' : 'properties'}`, variant: 'grey' }] : [];
});

const railSections = computed<TeamRailSection[]>(() => [
  // A backend that predates access requests sends none
  ...(team.value?.accessRequests?.length
    ? [{ id: 'team-requests', label: 'Requests', count: team.value.accessRequests.length }]
    : []),
  { id: 'team-manager', label: 'Manager', count: team.value && (team.value.manager ? 1 : 0) },
  {
    id: 'team-listing-contact',
    label: 'Listing contact',
    count: team.value && (team.value.listingContact.contact ? 1 : 0),
  },
  { id: 'team-portfolio', label: 'Portfolio', count: team.value && (team.value.portfolio ? 1 : 0) },
  { id: 'team-collaborators', label: 'Collaborators', count: team.value?.collaborators.length },
  { id: 'team-agents', label: 'Showing agents', count: team.value && agents.value?.agents.length },
]);
const listingContactDescription = computed(() =>
  team.value?.listingContact.shownOnListings
    ? `Renters see this person as the contact for this ${noun.value} on Zillow, Realtor.com, Zumper and Apartments.com. Their messages still come to Tallo, so Emily answers them.`
    : "Your listings go out under Tallo's name with Emily as the contact, so listing sites don't show this person.",
);
// It is set on the portfolio only, so the line names the portfolio, or says why the manager stands in
const listingContactSource = computed(() => {
  const portfolio = team.value?.portfolio;

  if (portfolio && team.value?.listingContact.setOnPortfolio) {
    return `Set on the ${portfolio.name} portfolio, for every ${noun.value} in it`;
  }

  return portfolio
    ? `The manager, since ${portfolio.name} doesn't name a listing contact`
    : `The manager, since this ${noun.value} isn't in a portfolio`;
});

const railNote = computed(() => {
  if (!team.value) {
    return undefined;
  }

  if (canChangeTeam.value) {
    return `Roles are per ${noun.value}. A portfolio's owner can change the team of any ${noun.value} in it, and its default collaborators join every ${noun.value} filed into it.`;
  }

  const whoMay = team.value.portfolio
    ? `Only this ${noun.value}'s manager, its portfolio's owner or an admin can change who works it.`
    : `Only this ${noun.value}'s manager or an admin can change who works it.`;
  const manager = team.value.manager;

  return manager && !isYou(manager.investorId) ? `${whoMay} Ask ${nameOf(manager)}.` : whoMay;
});

// What each seat already is on this unit, for the pickers
const collaboratorHints = computed(() =>
  Object.fromEntries(collaboratorIds.value.map((investorId) => [investorId, 'collaborator here'])),
);
const agentHints = computed(() =>
  Object.fromEntries(
    (agents.value?.agents ?? [])
      .filter((agent) => agent.investorId)
      .map((agent) => [agent.investorId, 'already a showing agent here']),
  ),
);
const rolesHere = computed(() => ({
  ...Object.fromEntries(collaboratorIds.value.map((investorId) => [investorId, 'Collaborator'])),
  ...(team.value?.manager ? { [team.value.manager.investorId]: 'Manager' } : {}),
}));

function isYou(investorId: string | null | undefined): boolean {
  return Boolean(investorId) && investorId === investorStore.investor?.id;
}

function isAgent(investorId: string): boolean {
  return Boolean(agents.value?.agents.some((agent) => agent.investorId === investorId));
}

/** The row's own role first, then any other role the same person holds on this unit */
function badgesFor(investorId: string | null, role: Role): TeamRoleBadge[] {
  const held: Role[] = [role];

  if (investorId) {
    if (role !== 'manager' && team.value?.manager?.investorId === investorId) held.push('manager');
    if (role !== 'collaborator' && collaboratorIds.value.includes(investorId)) held.push('collaborator');
    if (role !== 'showingAgent' && isAgent(investorId)) held.push('showingAgent');
  }

  return held.map((each) => teamRoleBadges[each]);
}

function agentName(agent: ShowingAgentDto): string {
  return getFullName(agent).trim();
}

/** A teammate is reached by email and phone; anyone else is a contact on the roster, with no account */
function agentDetails(agent: PropertyShowingAgentDto): string {
  const phone = agent.phone ? formatPhoneNumber(agent.phone) : undefined;

  if (!agent.investorId) {
    return [phone, 'not a Tallo user'].filter(Boolean).join(' · ');
  }

  const email = members.value.find((member) => member.investorId === agent.investorId)?.email;

  return [email, phone].filter(Boolean).join(' · ');
}

async function makeAgent(collaborator: TeamMemberDto) {
  try {
    await addAgent.mutateAsync({ subject: props.subject, agent: { investorId: collaborator.investorId } });
  } catch {
    // The mutation already showed the server's message
    return;
  }

  alertsStore.success(`<b>${escapeHtml(nameOf(collaborator))}</b> is now a showing agent here`);
}

async function makeMeTheAgent() {
  const investorId = investorStore.investor?.id;

  if (!investorId) {
    return;
  }

  try {
    await addAgent.mutateAsync({ subject: props.subject, agent: { investorId } });
  } catch {
    // The mutation already showed the server's message
    return;
  }

  alertsStore.success("You're now a showing agent here");
  // The empty state's button that was focused is gone with the empty state
  focusSection('team-agents');
}

function announceSaved(agent: ShowingAgentDto) {
  alertsStore.success(`<b>${escapeHtml(agentName(agent))}</b>'s contact details saved`);
}

async function grantRequest(request: PropertyAccessRequestDto) {
  answering.value = request.id;

  try {
    await addCollaborator.mutateAsync({ subject: props.subject, investorId: request.requester.investorId });
    alertsStore.success(`<b>${escapeHtml(nameOf(request.requester))}</b> can work on this ${noun.value} now`);
    focusSection('team-collaborators');
  } catch {
    // The mutation already showed the server's message
  } finally {
    answering.value = undefined;
  }
}

async function declineRequest(request: PropertyAccessRequestDto) {
  answering.value = request.id;

  try {
    await declineAccessRequest.mutateAsync({ subject: props.subject, requestId: request.id });
    alertsStore.success(`Declined <b>${escapeHtml(nameOf(request.requester))}</b>'s request`);
    focusSection('team-manager');
  } catch {
    // The mutation already showed the server's message
  } finally {
    answering.value = undefined;
  }
}

function confirmRemoveCollaborator(collaborator: TeamMemberDto) {
  const name = nameOf(collaborator);
  const staysAgent = isAgent(collaborator.investorId) ? ' They can still host tours of it.' : '';
  const leaves =
    props.subject.kind === 'building'
      ? "They can no longer change it. Their units and other buildings aren't affected."
      : "It leaves their home page and queues, and they can no longer change it. Their other units aren't affected.";

  pendingRemoval.value = {
    title: `Remove ${name} from this ${noun.value}?`,
    description: `${leaves}${staysAgent}`,
    run: () => removeCollaborator.mutateAsync({ subject: props.subject, investorId: collaborator.investorId }),
    done: `<b>${escapeHtml(name)}</b> removed from this ${noun.value}`,
    sectionId: 'team-collaborators',
  };
  confirmModalRef.value?.show();
}

function confirmRemoveAgent(agent: PropertyShowingAgentDto) {
  const name = agentName(agent);

  pendingRemoval.value = {
    title: `Remove ${name} as a showing agent?`,
    description: `Tallo stops offering them for new tours of this ${noun.value}. Tours already booked with them stay theirs, and they keep any other role here. You can add them back any time.`,
    run: () => removeAgent.mutateAsync({ subject: props.subject, showingAgentId: agent.id }),
    done: `<b>${escapeHtml(name)}</b> removed as a showing agent`,
    sectionId: 'team-agents',
  };
  confirmModalRef.value?.show();
}

async function runRemoval() {
  const removal = pendingRemoval.value;

  if (!removal) {
    return;
  }

  try {
    await removal.run();
  } catch {
    // The mutation already showed the server's message; the dialog stays open for another try
    return;
  }

  // Dismissed mid-request and reopened for someone else, the dialog is no longer this removal's to close
  if (pendingRemoval.value === removal) {
    completedRemoval = removal;
    confirmModalRef.value?.close();
  }

  alertsStore.success(removal.done);
}

/** The row that opened the dialog is gone with the removal, so the focus trap has nowhere to return the keyboard */
function onRemovalClosed() {
  const removal = completedRemoval;

  completedRemoval = undefined;
  pendingRemoval.value = undefined;

  if (removal) {
    focusSection(removal.sectionId);
  }
}

function focusSection(sectionId: string) {
  document.getElementById(sectionId)?.focus({ preventScroll: true });
}

// The rail marks the section being read: the last one whose top has passed the upper quarter of the
// viewport. At the bottom of the page the last section is marked, since a short one never gets that high.
const activeSection = ref('team-manager');
// Requests come first when someone is waiting, so an unscrolled page marks them
watch(
  () => Boolean(team.value?.accessRequests?.length),
  (waiting) => {
    if (waiting && window.scrollY === 0) activeSection.value = 'team-requests';
  },
  { immediate: true },
);
let frame = 0;
// A section picked in the rail stays marked while the page scrolls to it: on a short page it may never reach the top
let pinnedUntilSettled = false;
let settleTimer: ReturnType<typeof setTimeout> | undefined;

function holdPickedSection() {
  clearTimeout(settleTimer);
  settleTimer = setTimeout(() => (pinnedUntilSettled = false), 150);
}

function markActiveSection() {
  frame = 0;
  const threshold = window.innerHeight / 4;
  const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
  const present = railSections.value.filter(({ id }) => document.getElementById(id));

  if (!present.length) {
    return;
  }

  if (atBottom && window.scrollY > 0) {
    activeSection.value = present[present.length - 1].id;

    return;
  }

  const passed = present.filter(({ id }) => document.getElementById(id)!.getBoundingClientRect().top <= threshold);
  activeSection.value = (passed[passed.length - 1] ?? present[0]).id;
}

function onScroll() {
  if (pinnedUntilSettled) {
    holdPickedSection();

    return;
  }

  if (!frame) {
    frame = requestAnimationFrame(markActiveSection);
  }
}

onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }));
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
  cancelAnimationFrame(frame);
  clearTimeout(settleTimer);
});

function scrollToSection(sectionId: string) {
  const element = document.getElementById(sectionId);

  if (!element) {
    return;
  }

  activeSection.value = sectionId;
  pinnedUntilSettled = true;
  holdPickedSection();
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const top = element.getBoundingClientRect().top + window.scrollY - 16;
  window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
  // The next Tab carries on inside the chosen section, not back in the rail
  element.focus({ preventScroll: true });
}
</script>

<style lang="scss">
// Reassign and Add agent are short dialogs whose picker menu would be clipped by a scrolling body,
// so while they are short their body lets the menu out. Global because Modal teleports its tree
// out of any component's scope; declared here, where both dialogs are mounted.
.modal-content.team-picker-dialog .modal-body {
  overflow: visible;
}
</style>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.team-layout {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: var(--t-spacing-lg);
  align-items: start;

  @include tablet {
    grid-template-columns: minmax(0, 1fr);

    .team-rail {
      display: none;
    }
  }
}

// Empty states read as a note inside the section, not a banner across it
.team-empty {
  max-width: 32rem;
}
</style>
